import type { VoidComponent } from "solid-js";
import { createUniqueId, mergeProps, Show } from "solid-js";
import { Switch as ArkSwitch } from "@ark-ui/solid";
import InfoButton from "../infoButton/InfoButton";
import { isNil } from "lodash-es";
import Text from "../text/Text.tsx";

type ToggleProps = {
  label?: string;
  labelAlignment?: "left" | "right";
  description?: string;
  "aria-label"?: string;
  disabled?: boolean;
  checked: boolean;
  setChecked: (value: boolean) => void;
  info?: Explainer;
  block?: boolean;
};

type Explainer = {
  title: string;
  alt: string;
  description: string;
  href?: string;
};

export const Toggle: VoidComponent<ToggleProps> = (rawProps) => {
  const descriptionId = createUniqueId();
  const props = mergeProps(
    {
      label: undefined,
      labelAlignment: "left",
      disabled: false,
      info: undefined,
      "aria-label": undefined,
      block: false,
    },
    rawProps,
  );

  const toggle = (
    <ArkSwitch.Root
      disabled={props.disabled}
      checked={props.checked}
      onCheckedChange={(e) => props.setChecked(e.checked)}
      // Relative is needed for the hidden ArkSwitch
      class="relative flex items-center gap-2"
      classList={{
        "w-full justify-between": props.block,
        "flex-row-reverse": props.labelAlignment === "right",
      }}
      aria-label={props["aria-label"]}
    >
      <Show when={props.label}>
        {(label) => (
          <div class="flex min-w-0 shrink items-center gap-1">
            <ArkSwitch.Label
              asChild={(labelProps) => (
                <Text {...labelProps} text={label()} truncate class="shrink text-base-semibold text-secondary" />
              )}
            />
            <Show when={props.info}>
              {(info) => (
                <InfoButton
                  title={label()}
                  description={info().description}
                  ariaLabel={info().alt}
                  href={info().href}
                />
              )}
            </Show>
          </div>
        )}
      </Show>
      <ArkSwitch.Control class="flex h-4 w-7 shrink-0 cursor-pointer items-center rounded-full bg-surface-inactive px-0.5 checked:bg-surface-active-brand-harsh focussed:outline-default">
        <ArkSwitch.Thumb class="size-3 rounded-full bg-accent-white transition-transform duration-150 ease-out checked:translate-x-full" />
      </ArkSwitch.Control>
      <ArkSwitch.HiddenInput aria-describedby={!isNil(props.description) ? descriptionId : undefined} />
    </ArkSwitch.Root>
  );

  return (
    <Show when={props.description} fallback={toggle}>
      <div>
        {toggle}
        <p id={descriptionId} class="w-full pr-11 text-base-regular wrap-break-word text-secondary">
          {props.description}
        </p>
      </div>
    </Show>
  );
};
